import { useState, useEffect, useCallback } from 'react';
import {
  fetchAllWalletTokens,
  WalletTokenState,
  buildCustomLiquidationData,
  refetchTokenBalances,
} from '@/coinLiquidation/walletTokenFetcher';
import { SupportedNetWork } from '@/coinLiquidation/constants';
import { fetchSolanaWalletTokens } from '@/coinLiquidation/solanaUtils';
import { fetchSuiWalletTokens } from '@/coinLiquidation/suiUtils';
import { onGetTokensFromCustomLiquidation } from '@/coinLiquidation/utils';
import { getCachedTokens, cacheTokens } from '@/utils/tokenCache';
import { filterTokensByCMC } from '@/utils/tokenFilter';
import { WalletType } from '@/types/wallet';

interface UseWalletTokensProps {
  address: string | undefined;
  walletType: WalletType;
  fetchCMCTokenPrices: () => Promise<any>;
  setCustomLiquidation: (data: Record<string, unknown>) => void;
}

export const useWalletTokens = ({
  address,
  walletType,
  fetchCMCTokenPrices,
  setCustomLiquidation,
}: UseWalletTokensProps) => {
  const totalChains = walletType === 'SOL' || walletType === 'SUI' ? 1 : SupportedNetWork.length;
  const [walletTokenState, setWalletTokenState] = useState<WalletTokenState>({
    tokenByNetwork: {},
    tokensList: [],
    isLoading: false,
    chainStatuses: [],
    completedChains: 0,
    totalChains: totalChains,
  });

  const [isDataFromCache, setIsDataFromCache] = useState(false);
  const [refreshingTokens, setRefreshingTokens] = useState(false);

  useEffect(() => {
    setWalletTokenState({
      tokenByNetwork: {},
      tokensList: [],
      isLoading: false,
      chainStatuses: [],
      completedChains: 0,
      totalChains: totalChains,
    });
    setIsDataFromCache(false);
    setRefreshingTokens(false);
  }, [totalChains]);

  // Function to fetch tokens from wallet
  const fetchWalletTokens = useCallback(async (forceRefresh = true) => {
    if (!address) return;

    console.log('walletType', walletType, forceRefresh)
    try {
      // Check cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedData = getCachedTokens(address);
        if (cachedData) {
          const nextState =
            walletType === 'SOL' || walletType === 'SUI'
              ? cachedData
              : await filterTokensByCMC(cachedData, fetchCMCTokenPrices);
          setWalletTokenState(nextState);
          setIsDataFromCache(true);
          // Build custom liquidation data structure
          const customData = buildCustomLiquidationData(nextState);
          setCustomLiquidation(customData);
          return;
        }
      }

      setWalletTokenState(prev => ({ ...prev, isLoading: true }));
      setIsDataFromCache(false);

      if (walletType === 'SOL') {
        const walletState = await fetchSolanaWalletTokens(
          address,
          undefined,
          (progressState) => {
            setWalletTokenState(progressState)
          },
        )

        cacheTokens(address, walletState);
        setWalletTokenState(walletState);

        const customData = buildCustomLiquidationData(walletState);
        setCustomLiquidation(customData);
        return;
      }
      if (walletType === 'SUI') {
        const walletState = await fetchSuiWalletTokens(
          address,
          undefined,
          undefined,
          (progressState) => {
            setWalletTokenState(progressState)
          },
        )

        cacheTokens(address, walletState);
        setWalletTokenState(walletState);

        const customData = buildCustomLiquidationData(walletState);
        setCustomLiquidation(customData);
        return;
      }

      const walletState = await fetchAllWalletTokens(address, async (progressState) => {
        // Update UI with real-time progress (filtered)
        const filteredProgressState = await filterTokensByCMC(progressState, fetchCMCTokenPrices);
        setWalletTokenState(filteredProgressState);
      });

      // Filter the final wallet state
      const filteredWalletState = await filterTokensByCMC(walletState, fetchCMCTokenPrices);

      // Cache the fresh data
      cacheTokens(address, filteredWalletState);

      setWalletTokenState(filteredWalletState);

      // Build custom liquidation data structure
      const customData = buildCustomLiquidationData(filteredWalletState);
      setCustomLiquidation(customData);
    } catch (error) {
      setWalletTokenState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }));
    }
  }, [address, walletType, fetchCMCTokenPrices, setCustomLiquidation]);

  const handleSwapSuccess = useCallback(async (data: any, customLiquidation: Record<string, unknown>) => {
    setCustomLiquidation({});

    setRefreshingTokens(true);
    try {
      if (walletType === 'SOL' || walletType === 'SUI') {
        await fetchWalletTokens(true);
        return data;
      }

      const soldTokens = onGetTokensFromCustomLiquidation(customLiquidation);
      if (soldTokens.length > 0) {
        const updatedTokens = await refetchTokenBalances(soldTokens, address!, (updatedTokens) => {
        });

        setWalletTokenState(prevState => {
          const updatedWalletState = { ...prevState };
          updatedTokens.forEach(token => {
            const chainId = token.network.chainId.toString();
            if (updatedWalletState.tokenByNetwork[chainId]) {
              const tokenIndex = updatedWalletState.tokenByNetwork[chainId].findIndex(
                t => t.address === token.address
              );
              if (tokenIndex >= 0) {
                updatedWalletState.tokenByNetwork[chainId][tokenIndex] = token;
              }
            }
          });
          filterTokensByCMC(updatedWalletState, fetchCMCTokenPrices).then(filteredState => {
            setWalletTokenState(filteredState);
            cacheTokens(address!, filteredState);
          });
          return updatedWalletState;
        });
      }
    } catch (error) {
      // Failed to refresh token balances
    } finally {
      setRefreshingTokens(false);
    }

    return data;
  }, [address, walletType, fetchWalletTokens, fetchCMCTokenPrices, setCustomLiquidation]);

  useEffect(() => {
    if (address) {
      fetchWalletTokens();
    }
  }, [address, fetchWalletTokens]);

  return {
    walletTokenState,
    isDataFromCache,
    refreshingTokens,
    fetchWalletTokens,
    handleSwapSuccess,
  };
};
